/*
  ITGC Fulfill modal — the request is READ-ONLY. Only lines the approver APPROVED
  appear. Each is fulfilled with a note + a fulfilled date (pre-filled today,
  editable). Per-line Mark Fulfilled commits immediately.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { itgcService, AccessRequest, AccessLine } from '../../services/itgcService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import NoDataFound from '../common/NoDataFound';
import { statusVariant } from '../../utils/statusVariant';
import { usePermission } from '../../contexts/PermissionContext';
import extractApiError from '../../utils/apiError';

// Coerce any error to a string — the shared validation engine returns detail as
// an array/object on 422, which crashes <Toast> if rendered raw.
const extractErr = (e: any, fallback: string): string => {
  const s = extractApiError(e, '');
  if (s) return s;
  const errStr = e?.response?.data?.error;
  if (typeof errStr === 'string' && errStr) return errStr;
  return fallback;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

interface Props {
  requestId: number;
  onClose: () => void;
  onSaved: () => void;
}

const ITGCFulfillModal: React.FC<Props> = ({ requestId, onClose, onSaved }) => {
  const canFulfil = usePermission('ORG_ITGC_ACCESS_FULFILL', 'view+modify').allowed;

  const [req, setReq] = useState<AccessRequest | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [dates, setDates] = useState<Record<number, string>>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = async () => {
    try {
      const r = await itgcService.getRequest(requestId);
      setReq(r);
      // Default each line's date to today, but preserve any date the user has
      // already typed (a re-fetch after fulfilling one line must not reset the
      // dates on the other, still-pending, lines).
      setDates(prev => {
        const d = { ...prev };
        (r.lines || []).forEach(l => { if (!d[l.id]) d[l.id] = todayIso(); });
        return d;
      });
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, 'Failed to load request') });
    } finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only approved lines are fulfillable.
  const lines = useMemo(
    () => (req?.lines || []).filter(l => l.is_active === 1 && l.approval_decision === 'APPROVED'),
    [req],
  );

  const markDone = async (l: AccessLine) => {
    if (!canFulfil) { setToast({ type: 'error', message: 'Not authorised to fulfil' }); return; }
    const date = dates[l.id] || todayIso();
    setBusyId(l.id);
    try {
      await itgcService.fulfillLine(l.id, { fulfill_note: notes[l.id] || undefined, fulfilled_date: date });
      setToast({ type: 'success', message: 'Line fulfilled' });
      await load();            // refresh statuses; if all done the parent close will refetch board
      onSaved();
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, 'Fulfil failed') });
    } finally { setBusyId(null); }
  };

  if (!canFulfil) {
    return (
      <Modal isOpen onClose={onClose} size="md">
        <Modal.Header>Fulfil access request</Modal.Header>
        <Modal.Body><NoDataFound title="Not authorised" message="You are not authorised to fulfil ITGC access requests." /></Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen onClose={onClose} size="lg">
        <Modal.Header>
          <span className="flex items-center gap-2">
            Fulfil {req?.request_code || '…'}
            {req && <Badge variant={statusVariant(req.status)} size="sm">{req.status}</Badge>}
          </span>
        </Modal.Header>
        <Modal.Body>
          {!loaded ? <p className="text-sm text-gray-500">Loading…</p> : (
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Target</th>
                      <th className="table-list-th">Action</th>
                      <th className="table-list-th">Fulfilling note</th>
                      <th className="table-list-th">Fulfilled date</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                      <th className="table-list-th table-list-th--right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr><td colSpan={7} className="table-list-cell"><NoDataFound title="Nothing to fulfil" message="No approved lines on this request." /></td></tr>
                    )}
                    {lines.map((l, idx) => {
                      const done = l.fulfill_status === 'DONE' || l.fulfill_status === 'FAILED';
                      return (
                        <tr key={l.id} className="table-list-row" data-testid={`itgc-fulfill-line-${l.id}`}>
                          <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                          <td className="table-list-cell"><span className="table-list-cell-title">{l.target_label || `#${l.target_id}`}</span></td>
                          <td className="table-list-cell">
                            <span className={`status-badge xs ${l.action === 'GRANT' ? 'success' : l.action === 'REVOKE' ? 'danger' : 'neutral'}`}>{l.action}</span>
                          </td>
                          <td className="table-list-cell">
                            <FormControl.Input
                              type="text"
                              value={notes[l.id] || ''}
                              onChange={e => setNotes(p => ({ ...p, [l.id]: e.target.value }))}
                              disabled={done || busyId === l.id}
                              placeholder="Optional"
                              data-testid={`itgc-fulfill-note-${l.id}`}
                            />
                          </td>
                          <td className="table-list-cell">
                            <FormControl.DatePicker
                              value={dates[l.id] || todayIso()}
                              onChange={e => setDates(p => ({ ...p, [l.id]: e.target.value }))}
                              disabled={done || busyId === l.id}
                              data-testid={`itgc-fulfill-date-${l.id}`}
                            />
                          </td>
                          <td className="table-list-cell">
                            {done ? <Badge variant={statusVariant(l.fulfill_status === 'DONE' ? 'CLOSED' : 'REJECTED')} size="sm">{l.fulfill_status}</Badge>
                                  : <Badge variant="warning" size="sm">PENDING</Badge>}
                          </td>
                          <td className="table-list-cell table-list-cell--actions">
                            {!done && (
                              <Button variant="fill" icon={Check} disabled={busyId === l.id} onClick={() => markDone(l)} data-testid={`itgc-fulfill-done-${l.id}`}>
                                Fulfil
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default ITGCFulfillModal;
